/**
 * codebaseWorkspace — Browser-side "Open Workspace" command for Codebase entities.
 *
 * Reads the Codebase entity's skeleton.workspace.windows array and opens each
 * window in the active Space using existing browser-side commands.
 *
 * Also listens for the 'codebase:open-workspace' custom event dispatched by the
 * CodebaseView "Open Workspace" header button.
 *
 * Window kinds handled:
 *   codebase_dashboard → space_open_window({ entityId: codebaseId })
 *   terminal           → space_open_window for the dev terminal + optional startCommand
 *   browser_tab        → browserOpenTab
 *   entity             → space_open_window
 *
 * [CodebaseWorkspace] log prefix used throughout.
 */
import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { createFileLogger } from '../../utils/logger';
import useEntityStore from '../../stores/entityStore';
import { useCommandRegistryStore } from '../../stores/commandRegistryStore';

const log = createFileLogger('CODEBASE_WORKSPACE');
let codebaseWorkspaceListenerAttached = false;

function joinPath(parent: string, child = ''): string {
  if (!child) return parent;
  return `${parent.replace(/\/+$/, '')}/${child.replace(/^\/+/, '')}`;
}

const codebaseOpenWorkspaceCommand: SelfContainedCommand = {
  id: 'codebase_open_workspace',
  description: 'Open all workspace preset windows (dashboard, terminal, browser tabs) for a Codebase entity',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'codebaseId',
        type: 'string',
        description: 'The _id of the Codebase entity to open the workspace for',
        required: true,
      },
    ],
  },

  examples: { good: [], bad: [] },
  performance: { expectedDuration: 2000, complexity: 'high' },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { codebaseId } = params;
    if (!codebaseId) return createErrorResult('codebaseId is required', 'codebaseId is required', 'validation');

    const entityStore = useEntityStore.getState();
    const codebase = entityStore.entities.find((e: any) => e._id === codebaseId || e.llmId === codebaseId);

    if (!codebase) {
      return createErrorResult(`Codebase entity not found in store: ${codebaseId}`, `Codebase entity not found in store: ${codebaseId}`);
    }

    const resolvedId = codebase._id;
    const skeleton = codebase.skeleton ?? {};
    const windows: any[] = skeleton.workspace?.windows ?? [];
    const registry = useCommandRegistryStore.getState();

    const opened: Array<{ kind: string; entityId?: string; url?: string }> = [];
    const total = windows.length;

    log.log(`[CodebaseWorkspace] opening workspace codebaseId=${resolvedId} windows=${total}`);

    // If no preset, just open the dashboard itself
    if (!total) {
      await registry.executeCommand('space_open_window', { entityId: resolvedId });
      return createSuccessResult('Opened codebase dashboard', { opened: [{ kind: 'codebase_dashboard', entityId: resolvedId }] });
    }

    for (let i = 0; i < windows.length; i++) {
      const win = windows[i];
      const kind: string = win.kind;

      try {
        if (kind === 'codebase_dashboard') {
          await registry.executeCommand('space_open_window', { entityId: resolvedId });
          opened.push({ kind, entityId: resolvedId });
          log.log(`[CodebaseWorkspace] opened codebase_dashboard entityId=${resolvedId} (${i + 1}/${total})`);

        } else if (kind === 'terminal') {
          // Terminal entities for this codebase may already exist in the store
          const cwd = skeleton.localPath
            ? joinPath(skeleton.localPath, win.cwdRelative)
            : undefined;

          // Dispatch a terminal:create-and-run event (handled by TerminalApp / SpaceChat listeners)
          window.dispatchEvent(new CustomEvent('terminal:create-and-run', {
            detail: {
              name: win.name || `${codebase.name} terminal`,
              cwd,
              command: win.startCommand || undefined,
            },
          }));
          opened.push({ kind });
          log.log(`[CodebaseWorkspace] dispatched terminal:create-and-run name="${win.name}" (${i + 1}/${total})`);

        } else if (kind === 'browser_tab') {
          const url: string = win.url;
          if (url) {
            await registry.executeCommand('browser_open_tab', { url });
            opened.push({ kind, url });
            log.log(`[CodebaseWorkspace] opened browser_tab url=${url} (${i + 1}/${total})`);
          }

        } else if (kind === 'entity') {
          const entityId: string = win.entityId;
          if (entityId) {
            await registry.executeCommand('space_open_window', { entityId });
            opened.push({ kind, entityId });
            log.log(`[CodebaseWorkspace] opened entity entityId=${entityId} (${i + 1}/${total})`);
          }
        }
      } catch (err: any) {
        log.warn(`[CodebaseWorkspace] window ${i + 1}/${total} kind=${kind} failed: ${err.message}`);
      }
    }

    log.log(`[CodebaseWorkspace] ✅ done opened=${opened.length}/${total} codebaseId=${resolvedId}`);
    return createSuccessResult(
      `Opened ${opened.length} workspace windows for "${codebase.name}"`,
      { opened, codebaseId: resolvedId },
    );
  },
};

export const codebaseWorkspaceCommands: SelfContainedCommand[] = [
  codebaseOpenWorkspaceCommand,
];

/**
 * Sets up a listener for the 'codebase:open-workspace' CustomEvent dispatched by
 * the CodebaseView "Open Workspace" header button. Bridges the UI button to the
 * command registry without requiring a prop-drilled callback.
 */
export function setupCodebaseWorkspaceEventListener() {
  if (codebaseWorkspaceListenerAttached) return;
  codebaseWorkspaceListenerAttached = true;

  window.addEventListener('codebase:open-workspace', async (e: Event) => {
    const detail = (e as CustomEvent).detail ?? {};
    const { codebaseId } = detail;
    if (!codebaseId) return;

    log.log(`[CodebaseWorkspace] received codebase:open-workspace event codebaseId=${codebaseId}`);
    try {
      const registry = useCommandRegistryStore.getState();
      await registry.executeCommand('codebase_open_workspace', { codebaseId });
    } catch (err: any) {
      log.error(`[CodebaseWorkspace] event handler failed: ${err.message}`);
    }
  });
}
